import { Product, ProductTenor } from '@protos/product';
import { OrderBookDepthGroup, OrderBookDepthResponse } from '@protos/v2/orderBookDepth';
import { LadderBarScalingMode, LadderEntry, LiveLadderData } from '../LadderTypes';
import { Side } from '@protos/trading';

const validateTickSize = (tickSize: number) => {
  if (tickSize > 1) {
    throw new Error('Tick size is greater than 1!');
  } else if (tickSize <= 0) {
    throw new Error('Tick size is zero or less!');
  } else if (Math.log10(tickSize) % 1 !== 0) {
    console.warn('Tick size is not a power of 10 - is this bad?');
  }
};

const tickSizeToDecimalPlaces = (tickSize: number) => {
  return Math.abs(Math.log10(tickSize));
};

export const getTicksBetweenPrices = (priceA: number, priceB: number, tickSize: number) => {
  const min = Math.min(priceA, priceB);
  const max = Math.max(priceA, priceB);
  return Math.floor((max - min) * (1 / tickSize)) - 1;
};

export const orderBookDepthResponseToLiveLadderData = (
  response: OrderBookDepthResponse,
  product: Product,
  tenor: ProductTenor,
  exchange: string,
  tabIndex: number,
  lastData?: LiveLadderData,
  scalingMode?: LadderBarScalingMode
): LiveLadderData | null => {
  const filtered = response.filter((group: OrderBookDepthGroup) => {
    //TODO: proper codes here
    return group.order_book.symbol === product.symbol + tenor.code && group.order_book.exchange === exchange;
  });

  if (filtered.length === 0) {
    return null;
  }

  // Construct the data for the ladder
  const ladder: LadderEntry[] = filtered
    .map(g => g.order_book.bids)
    .concat(filtered.map(g => g.order_book.asks))
    .flat()
    .map(row => {
      return {
        price: Number(row.price),
        quantity: Number(row.amount),
        side: row.side as Side,
        hasBid: row.side === Side.BUY,
        hasAsk: row.side === Side.SELL,
        isFairValue: false,
        expansionTicks: 0,
      };
    })
    .filter(r => !isNaN(r.price) && !isNaN(r.quantity))
    // Sort ascending to start with
    .sort((a, b) => (b.price < a.price ? 1 : -1));

  const minQuantity = ladder.reduce((memo, current) => {
    return Math.min(memo, current.quantity);
  }, 99999);
  const maxQuantity = ladder.reduce((memo, current) => {
    return Math.max(memo, current.quantity);
  }, 0);
  const total = ladder.reduce((memo, current) => {
    return memo + current.quantity;
  }, 0);

  let adjustedMax = maxQuantity;
  let samples: number[] = [adjustedMax];
  if (lastData && lastData.lastMaxValues) {
    samples = lastData.lastMaxValues.concat([adjustedMax]);
  }

  if (scalingMode !== undefined) {
    switch (scalingMode) {
      case LadderBarScalingMode.LatestOnly:
        samples = [adjustedMax];
        break;
      case LadderBarScalingMode.LastFive:
        samples = samples.slice(-5);
        break;
      case LadderBarScalingMode.LastTen:
        samples = samples.slice(-10);
        break;
    }
  }

  adjustedMax =
    samples.reduce((prev, current) => {
      return prev + current;
    }, 0) / samples.length;

  adjustedMax = Math.max(maxQuantity, adjustedMax);

  if (ladder.length === 0) return null;

  const out: LiveLadderData = {
    index: tabIndex,
    ladder,
    total,
    min: minQuantity,
    max: adjustedMax,
    fairValueIndex: -1,
    lastUpdateTime: new Date().getTime(),
    lastMaxValues: samples,
  };

  // Fill the ticks between the rows
  const filledLadder: LadderEntry[] = [];

  const dataWithSpread = updateLiveLadderSpread(out, lastData);

  // Historical bid will be filled after the above call
  const { bid, ask, hasAnyAsks, hasAnyBids } = dataWithSpread.historicalSpread!;

  const highestAsk = ask.max;
  const lowestAsk = ask.min;
  const highestBid = bid.max;
  const lowestBid = bid.min;
  const tickSize = product.tick_size;

  validateTickSize(tickSize);
  const numDecimalPlaces = tickSizeToDecimalPlaces(tickSize);

  const fairValueDistance = hasAnyAsks && hasAnyBids ? getTicksBetweenPrices(highestBid, lowestAsk, tickSize) : 0;
  const requiredAskRows = hasAnyAsks ? getTicksBetweenPrices(lowestAsk, highestAsk, tickSize) : 0;
  const requiredBidRows = hasAnyBids ? getTicksBetweenPrices(lowestBid, highestBid, tickSize) : 0;

  const rowsToFill = getTicksBetweenPrices(lowestBid, highestAsk, tickSize);
  const fairValueIndex = Math.max(requiredAskRows, requiredBidRows) + Math.ceil(fairValueDistance * 0.5);

  for (let i = 0; i < rowsToFill; i++) {
    const tickValue = (Number(lowestBid.toFixed(numDecimalPlaces)) + i * tickSize).toFixed(numDecimalPlaces);
    const dataRow = out.ladder.find(r => r.price.toFixed(numDecimalPlaces) === tickValue);
    if (dataRow) {
      filledLadder.push({ ...dataRow, isFairValue: i === fairValueIndex });
    } else {
      const side: Side = i <= fairValueIndex ? Side.BUY : Side.SELL;
      filledLadder.push({
        price: Number(tickValue),
        quantity: 0,
        side,
        hasBid: false,
        hasAsk: false,
        isFairValue: i === fairValueIndex,
        expansionTicks: 0,
      });
    }
  }

  return { ...dataWithSpread };
};

export const updateLiveLadderSpread = (data: LiveLadderData, prevData: LiveLadderData | undefined): LiveLadderData => {
  const bids = data.ladder.filter(k => k.side === Side.BUY);
  const asks = data.ladder.filter(k => k.side === Side.SELL);

  const minBid = bids.reduce((memo, bid) => {
    return Math.min(memo, bid.price);
  }, Infinity);
  const maxBid = bids.reduce((memo, bid) => {
    return Math.max(memo, bid.price);
  }, -Infinity);
  const minAsk = asks.reduce((memo, ask) => {
    return Math.min(memo, ask.price);
  }, Infinity);
  const maxAsk = asks.reduce((memo, ask) => {
    return Math.max(memo, ask.price);
  }, -Infinity);

  const nextSpread = {
    bid: { min: minBid, max: maxBid },
    ask: { min: minAsk, max: maxAsk },
    hasAnyBids: bids.length !== 0,
    hasAnyAsks: asks.length !== 0,
  };

  const allTimeSpread = prevData?.historicalSpread || nextSpread;

  data.currentSpread = nextSpread;

  if (allTimeSpread) {
    data.historicalSpread = {
      bid: {
        min: nextSpread.bid.min,
        max: nextSpread.bid.max,
      },
      ask: {
        min: nextSpread.ask.min,
        max: nextSpread.ask.max,
      },
      hasAnyBids: data.historicalSpread?.hasAnyBids || nextSpread.hasAnyBids,
      hasAnyAsks: data.historicalSpread?.hasAnyAsks || nextSpread.hasAnyAsks,
    };
  } else {
    data.historicalSpread = data.currentSpread;
  }

  return data;
};
